<template>
  <div class="device-detail">
    <el-page-header @back="goBack" content="设备详情" title="返回">
      <template #extra>
        <el-button @click="refreshDevice" :loading="loading">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </template>
    </el-page-header>

    <el-card v-loading="loading" style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span>基本信息</span>
          <el-tag :type="device.armStatus === 'armed' ? 'success' : 'info'">
            {{ device.armStatus === 'armed' ? '已布防' : '未布防' }}
          </el-tag>
        </div>
      </template>

      <el-descriptions :column="2" border>
        <el-descriptions-item label="设备名称">{{ device.deviceName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="设备ID">{{ device.deviceId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="设备型号">{{ device.deviceModel || device.productModel || '-' }}</el-descriptions-item>
        <el-descriptions-item label="产品名称">{{ device.productName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="是否网关">
          <el-tag :type="isGateway ? 'warning' : 'info'" size="small">
            {{ isGateway ? '是' : '否' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="所属家庭">{{ device.familyName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="安装位置">{{ device.location || device.locationInfo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="在线状态">
          <el-tag :type="getStatusType(device.status)">
            {{ getStatusText(device.status) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="电池电量">
          <div v-if="device.batteryLevel > 0 || device.batteryVol" style="display: flex; align-items: center; gap: 16px;">
            <el-progress
              v-if="device.batteryLevel > 0"
              :percentage="device.batteryLevel"
              :color="getBatteryColor(device.batteryLevel)"
              :format="() => `${device.batteryLevel}%`"
              style="flex: 1; max-width: 200px;"
            />
            <span v-if="device.batteryVol" style="color: #909399; font-size: 12px;">
              电压: {{ device.batteryVol }}V
            </span>
          </div>
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="信号强度">
          <span v-if="device.signalStrength">
            {{ device.signalStrength }} ({{ formatSignalStrength(device.signalStrength) }})
            <el-tag :type="getSignalQualityType(device.signalStrength)" size="small" style="margin-left: 8px;">
              {{ getSignalQualityText(device.signalStrength) }}
            </el-tag>
          </span>
          <span v-else>-</span>
        </el-descriptions-item>
        <el-descriptions-item label="固件版本">{{ device.firmwareVersion || '-' }}</el-descriptions-item>
        <el-descriptions-item label="最后上线时间" :span="2">
          {{ device.lastHeartbeat ? formatDateTime(device.lastHeartbeat) : (device.lastOnlineTime ? formatDateTime(device.lastOnlineTime) : '-') }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间" :span="2">
          {{ device.createdAt ? formatDateTime(device.createdAt) : '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="设备描述" :span="2">
          {{ device.description || '-' }}
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 设备控制卡片（仅对配置了AEP的产品显示，如NB烟感、NB燃气） -->
    <el-card v-if="productConfig && productConfig.aepProductId" style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span>设备控制</span>
        </div>
      </template>

      <el-space wrap :size="20">
        <el-button
          type="warning"
          @click="muteDevice"
          :loading="muteLoading"
          size="large"
        >
          <el-icon><Bell /></el-icon>
          消音
        </el-button>

        <el-button
          type="danger"
          @click="resetDevice"
          :loading="resetLoading"
          size="large"
        >
          <el-icon><RefreshRight /></el-icon>
          复位
        </el-button>

        <el-button
          type="primary"
          @click="queryDevice"
          :loading="queryLoading"
          size="large"
        >
          <el-icon><Search /></el-icon>
          查询状态
        </el-button>
      </el-space>

      <!-- 指令执行结果 -->
      <el-card v-if="commandResult" style="margin-top: 20px;" shadow="never">
        <template #header>
          <span>指令执行结果</span>
        </template>
        <el-result
          :icon="commandResult.success ? 'success' : 'error'"
          :title="commandResult.message"
        >
          <template #extra>
            <el-descriptions :column="1" border size="small">
              <el-descriptions-item label="设备ID">{{ commandResult.deviceId }}</el-descriptions-item>
              <el-descriptions-item label="设备名称">{{ commandResult.deviceName }}</el-descriptions-item>
              <el-descriptions-item label="执行时间">{{ formatDateTime(new Date()) }}</el-descriptions-item>
              <el-descriptions-item label="详细信息" v-if="commandResult.commandResult">
                <pre style="margin: 0;">{{ JSON.stringify(commandResult.commandResult, null, 2) }}</pre>
              </el-descriptions-item>
            </el-descriptions>
          </template>
        </el-result>
      </el-card>
    </el-card>

    <!-- 跌倒雷达设备控制 -->
    <el-card v-if="isTumbleRadar" style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span><el-icon><Switch /></el-icon> 跌倒雷达控制</span>
          <el-tag type="success" size="small">tumble_radar</el-tag>
        </div>
      </template>

      <!-- 设备重启 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">设备重启</span>
          <el-button
            type="danger"
            size="small"
            :loading="rebootLoading"
            @click="rebootDevice"
          >
            重启设备
          </el-button>
        </div>
      </div>

      <el-divider />

      <!-- 无人报警 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">无人报警</span>
          <el-switch
            :model-value="nooneSwitch"
            :loading="nooneSwitchLoading"
            @change="toggleNooneAlarm"
          />
        </div>
        <div v-if="nooneSwitch" class="tumble-sub-control">
          <div class="tumble-control-row">
            <span class="tumble-sub-label">触发时间（分钟）</span>
            <span class="tumble-value">{{ nooneRemaintime }} 分钟</span>
          </div>
          <div class="tumble-slider-row">
            <el-slider
              v-model="nooneRemaintime"
              :min="5"
              :max="120"
              :step="5"
              :show-tooltip="true"
              style="flex: 1;"
            />
            <el-button
              type="primary"
              size="small"
              :loading="nooneRemaintimeLoading"
              @click="saveNooneRemaintime"
              style="margin-left: 12px;"
            >
              保存
            </el-button>
          </div>
        </div>
      </div>

      <el-divider />

      <!-- 有人报警 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">有人报警</span>
          <el-switch
            :model-value="someoneSwitch"
            :loading="someoneSwitchLoading"
            @change="toggleSomeoneAlarm"
          />
        </div>
      </div>

      <el-divider />

      <!-- 有人探测高限 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">有人探测高限</span>
          <span class="tumble-value">{{ someoneHighLimit }} CM</span>
        </div>
        <div class="tumble-slider-row">
          <el-slider
            v-model="someoneHighLimit"
            :min="1"
            :max="300"
            :step="1"
            :show-tooltip="true"
            style="flex: 1;"
          />
          <el-button
            type="primary"
            size="small"
            :loading="someoneHighLimitLoading"
            @click="saveSomeoneHighLimit"
            style="margin-left: 12px;"
          >
            保存
          </el-button>
        </div>
      </div>

      <el-divider />

      <!-- 吸顶安装高度 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">吸顶安装高度</span>
          <span class="tumble-value">{{ deviceHeight }} CM</span>
        </div>
        <div class="tumble-slider-row">
          <el-slider
            v-model="deviceHeight"
            :min="200"
            :max="300"
            :step="1"
            :show-tooltip="true"
            style="flex: 1;"
          />
          <el-button
            type="primary"
            size="small"
            :loading="deviceHeightLoading"
            @click="saveDeviceHeight"
            style="margin-left: 12px;"
          >
            保存
          </el-button>
        </div>
      </div>

      <el-divider />

      <!-- 跌倒报警 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">跌倒报警</span>
          <el-switch
            :model-value="tumbleAlarmSwitch"
            :loading="tumbleAlarmSwitchLoading"
            @change="toggleTumbleAlarm"
          />
        </div>
        <div v-if="tumbleAlarmSwitch" class="tumble-sub-control">
          <div class="tumble-control-row">
            <span class="tumble-sub-label">跌倒时长（秒）</span>
            <span class="tumble-value">{{ tumbleTime }} 秒</span>
          </div>
          <div class="tumble-slider-row">
            <el-slider
              v-model="tumbleTime"
              :min="5"
              :max="180"
              :step="5"
              :show-tooltip="true"
              style="flex: 1;"
            />
            <el-button
              type="primary"
              size="small"
              :loading="tumbleTimeLoading"
              @click="saveTumbleTime"
              style="margin-left: 12px;"
            >
              保存
            </el-button>
          </div>

          <div class="tumble-control-row" style="margin-top: 12px;">
            <span class="tumble-sub-label">跌倒灵敏度</span>
            <span class="tumble-value">{{ sensitiveLabels[tumbleSensitive] }}</span>
          </div>
          <div class="tumble-slider-row">
            <el-slider
              v-model="tumbleSensitive"
              :min="0"
              :max="3"
              :step="1"
              :show-tooltip="true"
              :format-tooltip="(val) => sensitiveLabels[val]"
              :marks="{ 0: '最低', 1: '低', 2: '中', 3: '高' }"
              style="flex: 1;"
            />
            <el-button
              type="primary"
              size="small"
              :loading="tumbleSensitiveLoading"
              @click="saveTumbleSensitive"
              style="margin-left: 12px;"
            >
              保存
            </el-button>
          </div>
        </div>
      </div>

      <el-divider />

      <!-- 静止驻留报警 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">静止驻留报警</span>
          <el-switch
            :model-value="pakingSwitch"
            :loading="pakingSwitchLoading"
            @change="togglePakingAlarm"
          />
        </div>
        <div v-if="pakingSwitch" class="tumble-sub-control">
          <div class="tumble-control-row">
            <span class="tumble-sub-label">驻留时长（秒）</span>
            <span class="tumble-value">{{ pakingTime }} 秒</span>
          </div>
          <div class="tumble-slider-row">
            <el-slider
              v-model="pakingTime"
              :min="60"
              :max="3600"
              :step="60"
              :show-tooltip="true"
              style="flex: 1;"
            />
            <el-button
              type="primary"
              size="small"
              :loading="pakingTimeLoading"
              @click="savePakingTime"
              style="margin-left: 12px;"
            >
              保存
            </el-button>
          </div>
        </div>
      </div>
    </el-card>

    <!-- 声光报警器设备控制 -->
    <el-card v-if="isAlarmSiren" style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span><el-icon><Bell /></el-icon> 声光报警器控制</span>
          <el-tag type="success" size="small">alarm_siren</el-tag>
        </div>
      </template>

      <!-- 报警开关 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">报警开关</span>
          <el-button
            :type="alarmSwitch ? 'danger' : 'primary'"
            size="small"
            :loading="alarmSwitchLoading"
            @click="toggleAlarmSwitch"
          >
            {{ alarmSwitch ? '停止报警' : '启动报警' }}
          </el-button>
        </div>
      </div>

      <el-divider />

      <!-- 报警时长 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">报警时长</span>
          <span class="tumble-value">{{ alarmTimeLabels[alarmTimeSetting] }}</span>
        </div>
        <div class="tumble-slider-row" style="margin-top: 8px;">
          <el-radio-group v-model="alarmTimeSetting" size="small">
            <el-radio-button :value="0">1分钟</el-radio-button>
            <el-radio-button :value="1">5分钟</el-radio-button>
            <el-radio-button :value="2">15分钟</el-radio-button>
            <el-radio-button :value="3">30分钟</el-radio-button>
            <el-radio-button :value="4">不自动关闭</el-radio-button>
          </el-radio-group>
          <el-button
            type="primary"
            size="small"
            :loading="alarmTimeLoading"
            @click="saveAlarmTime"
            style="margin-left: 12px;"
          >
            保存
          </el-button>
        </div>
      </div>

      <el-divider />

      <!-- 音量设置 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">音量设置</span>
          <span class="tumble-value">{{ voiceLabels[voiceSetting] }}</span>
        </div>
        <div class="tumble-slider-row" style="margin-top: 8px;">
          <el-radio-group v-model="voiceSetting" size="small">
            <el-radio-button :value="0">静音(只有光)</el-radio-button>
            <el-radio-button :value="1">低</el-radio-button>
            <el-radio-button :value="2">中</el-radio-button>
            <el-radio-button :value="3">高</el-radio-button>
          </el-radio-group>
          <el-button
            type="primary"
            size="small"
            :loading="voiceLoading"
            @click="saveVoice"
            style="margin-left: 12px;"
          >
            保存
          </el-button>
        </div>
      </div>

      <el-divider />

      <!-- 关联设备 -->
      <div class="tumble-control-group">
        <div class="tumble-control-row">
          <span class="tumble-label">关联设备（已选 {{ linkedDevices.length }} 台）</span>
          <el-button type="primary" size="small" :loading="linkedDeviceSaving" @click="saveLinkedDevices">
            保存关联
          </el-button>
        </div>
        <div style="margin-top: 8px; color: #909399; font-size: 12px;">
          勾选的设备触发报警时，将联动本声光报警器
        </div>
        <el-checkbox-group v-model="linkedDevices" style="margin-top: 12px;">
          <div v-for="dev in familyDeviceList" :key="dev.deviceId"
               style="padding: 6px 0; display: flex; align-items: center;">
            <el-checkbox :value="dev.deviceId">
              {{ dev.deviceName }}
              <el-tag size="small" style="margin-left: 4px;">{{ dev.deviceTypeName }}</el-tag>
              <el-tag :type="dev.status === 1 ? 'success' : 'info'" size="small" style="margin-left: 4px;">
                {{ dev.status === 1 ? '在线' : '离线' }}
              </el-tag>
            </el-checkbox>
          </div>
        </el-checkbox-group>
      </div>
    </el-card>

    <!-- 智能胸卡定时语音管理 -->
    <el-card v-if="isSmartCard" style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span><el-icon><Bell /></el-icon> 定时语音管理</span>
          <el-tag type="success" size="small">smart_card</el-tag>
        </div>
      </template>

      <div style="margin-bottom: 12px; color: #909399; font-size: 13px;">
        配置设备定时语音提醒，最多支持5组定时任务。保存后将自动下发到设备。
      </div>

      <div v-for="(item, index) in voiceSchedules" :key="index" style="margin-bottom: 16px; padding: 12px; background: #f5f7fa; border-radius: 8px;">
        <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 12px;">
          <el-tag size="small">定时{{ item.timerId }}</el-tag>

          <el-time-picker
            v-model="item.timeValue"
            format="HH:mm"
            placeholder="选择时间"
            size="small"
            style="width: 120px;"
          />

          <el-select
            v-model="item.weekDays"
            multiple
            placeholder="选择星期"
            size="small"
            style="min-width: 240px;"
          >
            <el-option
              v-for="opt in weekDayOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>

          <el-input
            v-model="item.timerContent"
            placeholder="语音提醒内容"
            size="small"
            style="min-width: 160px; flex: 1;"
          />

          <el-switch
            v-model="item.timerSwitch"
            :active-value="1"
            :inactive-value="0"
            active-text="开"
            inactive-text="关"
            size="small"
          />

          <el-button type="primary" size="small" :loading="item.saving" @click="saveVoiceSchedule(item)">
            保存
          </el-button>
          <el-button type="danger" size="small" :loading="item.deleting" @click="deleteVoiceSchedule(item, index)">
            删除
          </el-button>
        </div>
      </div>

      <el-button
        v-if="voiceSchedules.length < 5"
        type="primary"
        plain
        size="small"
        @click="addVoiceSchedule"
      >
        + 添加定时
      </el-button>
    </el-card>

    <!-- 设备定位地图（有定位数据时显示） -->
    <el-card v-if="devicePosition" style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span><el-icon><Location /></el-icon> 设备定位</span>
          <div>
            <el-tag size="small" style="margin-right: 8px;">
              {{ devicePosition.accessTypeName || '未知' }}定位
            </el-tag>
            <el-tag v-if="devicePosition.powerPercent" type="success" size="small" style="margin-right: 8px;">
              电量 {{ devicePosition.powerPercent }}%
            </el-tag>
            <el-button size="small" type="primary" @click="loadPositionHistory" :loading="positionLoading">
              历史轨迹
            </el-button>
          </div>
        </div>
      </template>

      <!-- 地图容器 -->
      <div v-if="devicePosition.latitude && devicePosition.longitude"
           id="position-map-container" class="position-map-container">
      </div>
      <div v-else class="position-no-coords">
        <el-icon :size="40" color="#909399"><Location /></el-icon>
        <p>已收到定位数据，坐标待解析</p>
        <el-button type="primary" size="small" :loading="positionResolving" @click="resolvePosition">
          重新解析坐标
        </el-button>
        <p v-if="positionResolveMsg" style="font-size: 12px; color: #e6a23c; margin-top: 8px;">
          {{ positionResolveMsg }}
        </p>
      </div>

      <!-- 定位详情 -->
      <div class="position-info-row">
        <el-descriptions :column="3" size="small" border style="margin-top: 12px;">
          <el-descriptions-item label="定位方式">{{ devicePosition.accessTypeName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="经度">{{ devicePosition.longitude || '待解析' }}</el-descriptions-item>
          <el-descriptions-item label="纬度">{{ devicePosition.latitude || '待解析' }}</el-descriptions-item>
          <el-descriptions-item label="电池电压">{{ devicePosition.batteryVoltage ? devicePosition.batteryVoltage + 'V' : '-' }}</el-descriptions-item>
          <el-descriptions-item label="电量">{{ devicePosition.powerPercent != null ? devicePosition.powerPercent + '%' : '-' }}</el-descriptions-item>
          <el-descriptions-item label="更新时间">{{ formatPositionTime(devicePosition.createdAt) }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- WiFi/基站原始数据 -->
      <el-collapse v-if="devicePosition.positionData" style="margin-top: 12px;">
        <el-collapse-item title="原始定位数据">
          <pre style="font-size: 12px; margin: 0;">{{ JSON.stringify(JSON.parse(devicePosition.positionData), null, 2) }}</pre>
        </el-collapse-item>
      </el-collapse>

      <!-- 历史轨迹列表 -->
      <div v-if="positionHistory.length > 0" style="margin-top: 16px;">
        <el-divider content-position="left">历史定位记录（共 {{ positionHistoryTotal }} 条）</el-divider>
        <el-table
          :data="positionHistory"
          size="small"
          max-height="400"
          highlight-current-row
          @row-click="onHistoryRowClick"
          style="width: 100%; cursor: pointer;"
        >
          <el-table-column label="时间" prop="createdAt" min-width="170">
            <template #default="{ row }">{{ formatPositionTime(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="定位方式" prop="accessTypeName" min-width="90" />
          <el-table-column label="经度" min-width="120">
            <template #default="{ row }">{{ row.longitude || '-' }}</template>
          </el-table-column>
          <el-table-column label="纬度" min-width="120">
            <template #default="{ row }">{{ row.latitude || '-' }}</template>
          </el-table-column>
          <el-table-column label="电量" min-width="70">
            <template #default="{ row }">{{ row.powerPercent != null ? row.powerPercent + '%' : '-' }}</template>
          </el-table-column>
        </el-table>
        <div style="display: flex; justify-content: flex-end; margin-top: 12px;">
          <el-pagination
            v-model:current-page="positionHistoryPage"
            :page-size="positionHistorySize"
            :total="positionHistoryTotal"
            layout="total, prev, pager, next"
            small
            @current-change="onPositionPageChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 围栏管理（有定位数据时显示） -->
    <el-card v-if="devicePosition" style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span><el-icon><Location /></el-icon> 围栏管理</span>
        </div>
      </template>

      <!-- 围栏类型选择 -->
      <div style="margin-bottom: 16px;">
        <el-radio-group v-model="geofenceFenceType" size="small">
          <el-radio-button label="circle">圆形围栏</el-radio-button>
          <el-radio-button label="polygon">多边形围栏</el-radio-button>
        </el-radio-group>
      </div>

      <!-- 圆形围栏 -->
      <div v-if="geofenceFenceType === 'circle'" class="geofence-form">
        <el-form :inline="true" size="small">
          <el-form-item label="围栏名称">
            <el-input v-model="geofenceName" placeholder="围栏名称" style="width: 150px;" />
          </el-form-item>
          <el-form-item label="半径(米)">
            <el-slider v-model="geofenceRadius" :min="100" :max="5000" :step="100"
              style="width: 200px;" show-input input-size="small" />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="addCircleGeofence" :loading="geofenceSaving">
              以设备当前位置为中心创建
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 多边形围栏 -->
      <div v-if="geofenceFenceType === 'polygon'" style="margin-bottom: 8px;">
        <el-form :inline="true" size="small">
          <el-form-item label="围栏名称">
            <el-input v-model="geofenceName" placeholder="围栏名称" style="width: 150px;" />
          </el-form-item>
        </el-form>
        <el-alert type="warning" :closable="false" show-icon style="margin-bottom: 10px;">
          <template #title>请将设备当前位置包含在多边形区域内，否则设备会立即触发越界报警</template>
        </el-alert>
        <div class="geofence-draw-tip">
          点击地图添加顶点（至少3个点），围住设备当前位置
        </div>
      </div>

      <!-- 围栏地图（始终显示，用于查看已有围栏 + 多边形绘制） -->
      <div id="geofence-map-container" class="geofence-map-container"></div>

      <!-- 多边形绘制操作栏 -->
      <div v-if="geofenceFenceType === 'polygon'">
        <el-alert v-if="polygonPoints.length >= 3 && !polygonContainsDevice"
          type="error" :closable="false" show-icon style="margin-top: 8px;">
          <template #title>设备当前位置不在围栏内，保存后将立即触发越界报警</template>
        </el-alert>
        <div style="margin-top: 8px; display: flex; gap: 8px;">
          <el-button size="small" @click="undoPolygonPoint" :disabled="polygonPoints.length === 0">
            撤销上一点 ({{ polygonPoints.length }})
          </el-button>
          <el-button size="small" @click="clearPolygonPoints">清除所有</el-button>
          <el-button type="primary" size="small" @click="savePolygonGeofence"
            :disabled="polygonPoints.length < 3" :loading="geofenceSaving">
            保存多边形围栏
          </el-button>
        </div>
      </div>

      <!-- 已有围栏列表 -->
      <div v-if="geofenceList.length > 0" style="margin-top: 20px;">
        <el-divider content-position="left">已有围栏（{{ geofenceList.length }}）</el-divider>
        <el-table :data="geofenceList" size="small" style="width: 100%; cursor: pointer;"
          highlight-current-row @row-click="onGeofenceRowClick">
          <el-table-column label="名称" prop="name" min-width="120" />
          <el-table-column label="类型" min-width="80">
            <template #default="{ row }">
              <el-tag size="small" :type="row.fenceType === 'polygon' ? 'warning' : 'primary'">
                {{ row.fenceType === 'polygon' ? '多边形' : '圆形' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="详情" min-width="150">
            <template #default="{ row }">
              <span v-if="row.fenceType === 'polygon'">{{ getPolygonPointCount(row.polygonPath) }}个顶点</span>
              <span v-else>半径 {{ row.radius }}米</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" min-width="80">
            <template #default="{ row }">
              <el-tag :type="row.enabled === 1 ? 'success' : 'info'" size="small">
                {{ row.enabled === 1 ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" min-width="170">
            <template #default="{ row }">{{ formatDateTime(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="操作" min-width="100">
            <template #default="{ row }">
              <el-button type="danger" size="small" link @click.stop="deleteGeofence(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div style="margin-top: 6px; font-size: 12px; color: #909399;">
          点击围栏行可在地图上高亮定位
        </div>
      </div>
    </el-card>

    <!-- 产品配置信息（仅对配置了AEP的产品显示） -->
    <el-card v-if="productConfig && productConfig.aepProductId" style="margin-top: 20px;">
      <template #header>
        <span>产品AEP配置</span>
      </template>
      <el-descriptions :column="2" border size="small">
        <el-descriptions-item label="产品名称">{{ productConfig.productName }}</el-descriptions-item>
        <el-descriptions-item label="AEP产品ID">{{ productConfig.aepProductId || '未配置' }}</el-descriptions-item>
        <el-descriptions-item label="消音指令" :span="2">
          <pre style="margin: 0;">{{ formatCommand(productConfig.muteCommand) }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="复位指令" :span="2">
          <pre style="margin: 0;">{{ formatCommand(productConfig.resetCommand) }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="查询指令" :span="2">
          <pre style="margin: 0;">{{ formatCommand(productConfig.queryCommand) }}</pre>
        </el-descriptions-item>
      </el-descriptions>
    </el-card>

    <!-- 事件通知策略配置 -->
    <el-card style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span>事件通知策略</span>
        </div>
      </template>

      <el-alert
        title="事件通知策略说明"
        type="info"
        :closable="false"
        style="margin-bottom: 20px;"
      >
        <p>为每个事件类型单独配置通知策略。默认不通知，选择具体策略后该事件触发时将按所选策略发送通知。</p>
      </el-alert>

      <el-table :data="eventPolicies" style="width: 100%" v-loading="eventPolicyLoading">
        <el-table-column prop="eventType" label="事件类型" width="220">
          <template #default="scope">
            {{ scope.row.eventName || scope.row.eventType }}
          </template>
        </el-table-column>
        <el-table-column label="通知策略" min-width="200">
          <template #default="scope">
            <el-select
              v-model="scope.row.notificationPolicyId"
              placeholder="不通知"
              clearable
              size="small"
              style="width: 100%;"
              @change="(val) => saveEventPolicyInline(scope.row, val)"
            >
              <el-option label="不通知" :value="''" />
              <el-option
                v-for="policy in availablePolicies"
                :key="policy.id"
                :label="policy.name"
                :value="policy.id"
              />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="当前状态" width="120">
          <template #default="scope">
            <el-tag v-if="scope.row.configured && scope.row.notificationPolicyId" type="success" size="small">已配置</el-tag>
            <el-tag v-else type="info" size="small">不通知</el-tag>
          </template>
        </el-table-column>
      </el-table>

      <el-empty v-if="eventPolicies.length === 0 && !eventPolicyLoading" description="该设备未关联产品或产品未定义事件" />
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Refresh, Bell, RefreshRight, Search, Switch, Location } from '@element-plus/icons-vue'
import axios from 'axios'
import api, { deviceEventPolicyApi, productEventApi, notificationPolicyApi, deviceSettingApi } from '@/utils/api'

export default {
  name: 'DeviceDetail',
  components: {
    Refresh, Bell, RefreshRight, Search, Switch, Location
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(false)
    const muteLoading = ref(false)
    const resetLoading = ref(false)
    const queryLoading = ref(false)
    const device = ref({})
    const productConfig = ref(null)
    const commandResult = ref(null)
    const isGateway = ref(false)

    // ===== 事件通知策略 =====
    const eventPolicies = ref([])
    const eventPolicyLoading = ref(false)
    const availablePolicies = ref([])

    // 事件名称映射缓存（从数据库加载）
    const eventNameMap = ref({})

    const deviceId = route.params.id

    // ===== 跌倒雷达控制 =====
    const isTumbleRadar = ref(false)
    const IOT_DOWNSTREAM_BASE = 'http://iot.azxyun.com:8080/api/downstream'

    // 无人报警
    const nooneSwitch = ref(false)
    const nooneSwitchLoading = ref(false)
    const nooneRemaintime = ref(30)
    const nooneRemaintimeLoading = ref(false)

    // 有人报警
    const someoneSwitch = ref(false)
    const someoneSwitchLoading = ref(false)
    const someoneHighLimit = ref(150)
    const someoneHighLimitLoading = ref(false)

    // 吸顶安装高度
    const deviceHeight = ref(260)
    const deviceHeightLoading = ref(false)

    // 跌倒报警
    const tumbleAlarmSwitch = ref(false)
    const tumbleAlarmSwitchLoading = ref(false)
    const tumbleTime = ref(30)
    const tumbleTimeLoading = ref(false)
    const tumbleSensitive = ref(2)
    const tumbleSensitiveLoading = ref(false)

    // 静止驻留报警
    const pakingSwitch = ref(false)
    const pakingSwitchLoading = ref(false)
    const pakingTime = ref(600)
    const pakingTimeLoading = ref(false)

    // 设备重启
    const rebootLoading = ref(false)

    const sensitiveLabels = ['最低', '低', '中', '高']

    // ===== 声光报警器控制 =====
    const isAlarmSiren = ref(false)
    const alarmSwitch = ref(false)
    const alarmSwitchLoading = ref(false)
    const alarmTimeSetting = ref(0)
    const alarmTimeLoading = ref(false)
    const voiceSetting = ref(3)
    const voiceLoading = ref(false)
    const alarmTimeLabels = ['1分钟', '5分钟', '15分钟', '30分钟', '不自动关闭']
    const voiceLabels = ['静音(只有光)', '低', '中', '高']

    // 声光报警器 - 关联设备
    const linkedDevices = ref([])
    const familyDeviceList = ref([])
    const linkedDeviceSaving = ref(false)

    // ===== 智能胸卡定时语音 =====
    const isSmartCard = ref(false)
    const voiceSchedules = ref([])
    const weekDayOptions = [
      { value: 0x01, label: '周一' },
      { value: 0x02, label: '周二' },
      { value: 0x04, label: '周三' },
      { value: 0x08, label: '周四' },
      { value: 0x10, label: '周五' },
      { value: 0x20, label: '周六' },
      { value: 0x40, label: '周日' }
    ]

    // ===== 设备定位 =====
    const devicePosition = ref(null)
    const positionHistory = ref([])
    const positionLoading = ref(false)
    const positionMapInstance = ref(null)
    const positionMarker = ref(null)
    const positionInfoWindow = ref(null)
    const positionResolving = ref(false)
    const positionResolveMsg = ref('')
    const positionHistoryPage = ref(1)
    const positionHistorySize = 20
    const positionHistoryTotal = ref(0)

    // ===== 围栏管理 =====
    const geofenceList = ref([])
    const geofenceFenceType = ref('circle')
    const geofenceName = ref('')
    const geofenceRadius = ref(500)
    const geofenceSaving = ref(false)
    const polygonPoints = ref([])
    const polygonContainsDevice = ref(false)
    const geofenceMapInstance = ref(null)
    const geofencePolygonLayer = ref(null)
    const geofenceMarkerLayer = ref(null)
    const geofenceExistingLayers = ref([])
    const highlightedGeofenceId = ref(null)

    // 加载设备详情
    const loadDevice = async () => {
      loading.value = true
      try {
        const response = await api.get(`/admin/devices/${deviceId}`)
        if (response.success) {
          device.value = response.data

          // 如果设备关联了产品，加载产品配置
          if (device.value.productId) {
            await loadProductConfig(device.value.productId)
          }

          // 检查是否为跌倒雷达设备
          if (device.value.deviceTypeCode === 'tumble_radar') {
            isTumbleRadar.value = true
            await loadTumbleRadarSettings()
          } else {
            isTumbleRadar.value = false
          }

          // 检查是否为声光报警器设备
          if (device.value.deviceTypeCode === 'alarm_siren') {
            isAlarmSiren.value = true
            await loadAlarmSirenSettings()
          } else {
            isAlarmSiren.value = false
          }

          // 检查是否为智能胸卡设备
          if (device.value.deviceTypeCode === 'smart_card') {
            isSmartCard.value = true
            await loadVoiceSchedules()
          } else {
            isSmartCard.value = false
          }

          // 加载事件通知策略数据
          await loadAvailablePolicies()
          await loadProductEventsForNameMap()
          await loadEventPolicies()

          // 加载设备定位数据（胸牌等支持定位的设备）
          await loadDevicePosition()
          // 加载设备围栏列表
          await loadGeofences()
        } else {
          ElMessage.error(response.message || '加载设备信息失败')
        }
      } catch (error) {
        console.error('加载设备信息失败:', error)
        ElMessage.error('加载设备信息失败')
      } finally {
        loading.value = false
      }
    }

    // 加载产品配置
    const loadProductConfig = async (productId) => {
      try {
        const response = await api.get(`/products/${productId}`)
        if (response.success && response.data) {
          const product = response.data

          isGateway.value = product.isGateway === true

          let aepProductId = product.aepProductId
          let muteCommand = product.muteCommandConfig
          let resetCommand = product.resetCommandConfig
          let queryCommand = product.queryCommandConfig

          if (!aepProductId && product.specifications) {
            try {
              const specs = JSON.parse(product.specifications)
              aepProductId = specs.AEP_PRODUCT_ID
              if (specs.command) {
                muteCommand = muteCommand || specs.command.mute
                resetCommand = resetCommand || specs.command.reset
                queryCommand = queryCommand || specs.command.query
              }
            } catch (error) {
              console.error('解析产品配置失败:', error)
            }
          }

          productConfig.value = {
            productName: product.name,
            aepProductId,
            muteCommand,
            resetCommand,
            queryCommand
          }
        }
      } catch (error) {
        console.error('加载产品配置失败:', error)
      }
    }

    // ===== 事件通知策略方法 =====

    // 加载事件名称映射（根据产品ID从数据库加载）
    const loadProductEventsForNameMap = async () => {
      if (!device.value.productId) return
      try {
        const response = await productEventApi.getEventsByProductId(device.value.productId)
        if (response.success && response.data) {
          const map = {}
          for (const event of response.data) {
            map[event.eventCode] = event.eventName + (event.eventHex ? ' (' + event.eventHex + ')' : '')
          }
          eventNameMap.value = map
        }
      } catch (error) {
        console.error('加载事件名称映射失败:', error)
      }
    }

    // 加载事件通知策略配置（合并产品事件列表）
    const loadEventPolicies = async () => {
      if (!device.value.deviceId) return
      eventPolicyLoading.value = true
      try {
        // 并行加载产品事件列表和已配置的策略
        const [productEventsRes, policiesRes] = await Promise.all([
          device.value.productId ? productEventApi.getEventsByProductId(device.value.productId) : { success: true, data: [] },
          deviceEventPolicyApi.getDeviceEventPolicies(device.value.deviceId)
        ])

        const productEventsList = (productEventsRes.success && productEventsRes.data) ? productEventsRes.data : []
        const configuredPolicies = (policiesRes.success && policiesRes.data) ? policiesRes.data : []

        // 只保留事件名称包含"报警"、"预警"、"故障"、"事件"的项
        const notifyKeywords = ['报警', '预警', '故障', '事件']
        const filteredEventsList = productEventsList.filter(evt =>
          notifyKeywords.some(kw => evt.eventName && evt.eventName.includes(kw))
        )

        // 以产品事件为基础，合并已配置的策略
        const policyMap = {}
        for (const p of configuredPolicies) {
          policyMap[p.eventType] = p
        }

        const merged = filteredEventsList.map(evt => {
          const existing = policyMap[evt.eventCode]
          let policyIdForSelect = ''  // 默认空字符串表示不通知（无记录）
          if (existing && existing.notificationPolicyId) {
            policyIdForSelect = existing.notificationPolicyId  // 已配置具体策略
          }
          return {
            eventType: evt.eventCode,
            eventName: evt.eventName + (evt.eventHex ? ' (' + evt.eventHex + ')' : ''),
            notificationPolicyId: policyIdForSelect,
            notificationPolicyName: existing ? existing.notificationPolicyName : null,
            enabled: existing ? existing.enabled : true,
            configured: !!(existing && existing.notificationPolicyId)
          }
        })

        eventPolicies.value = merged
      } catch (error) {
        console.error('加载事件策略配置失败:', error)
      } finally {
        eventPolicyLoading.value = false
      }
    }

    // 加载可用的通知策略
    const loadAvailablePolicies = async () => {
      if (!device.value.familyId) return
      try {
        const response = await notificationPolicyApi.getEnabledNotificationPolicies(device.value.familyId)
        if (response.success) {
          availablePolicies.value = response.data || []
        }
      } catch (error) {
        console.error('加载通知策略列表失败:', error)
      }
    }

    // 保存事件策略（内联编辑）
    const saveEventPolicyInline = async (row, newValue) => {
      try {
        if (!newValue || newValue === '') {
          // 选择"不通知"，删除已有记录
          if (row.configured) {
            await deviceEventPolicyApi.deleteDeviceEventPolicy(device.value.deviceId, row.eventType)
            row.configured = false
            ElMessage.success('已设为不通知')
          }
        } else {
          // 保存具体策略
          const policies = [{
            eventType: row.eventType,
            notificationPolicyId: newValue,
            enabled: true
          }]
          const response = await deviceEventPolicyApi.batchSaveDeviceEventPolicies(device.value.deviceId, policies)
          if (response.success) {
            row.configured = true
            ElMessage.success('保存成功')
          } else {
            ElMessage.error(response.message || '保存失败')
            await loadEventPolicies()
          }
        }
      } catch (error) {
        console.error('保存事件策略配置失败:', error)
        ElMessage.error('保存失败')
        await loadEventPolicies()
      }
    }

    // 获取事件类型名称（从数据库加载的映射中查找）
    const getEventTypeName = (eventType) => {
      return eventNameMap.value[eventType] || eventType
    }

    // ===== 跌倒雷达方法 =====

    // 通用IoT下行指令发送
    const sendDownstream = async (command, val) => {
      const identCode = device.value.deviceId
      const url = `${IOT_DOWNSTREAM_BASE}/${identCode}/${command}`
      const data = val !== undefined ? { val } : {}
      const response = await axios.post(url, data, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 10000
      })
      return response.data
    }

    // 保存设置到数据库
    const saveDeviceSetting = async (key, value) => {
      try {
        await deviceSettingApi.saveSetting(device.value.deviceId, key, value)
      } catch (error) {
        console.error('保存设备设置失败:', error)
      }
    }

    // 加载跌倒雷达已保存的设置
    const loadTumbleRadarSettings = async () => {
      try {
        const response = await deviceSettingApi.getSettings(device.value.deviceId)
        if (response.success && response.data) {
          const s = response.data
          if (s.noone_switch !== undefined) nooneSwitch.value = s.noone_switch === '1'
          if (s.noone_alarm_remaintime !== undefined) nooneRemaintime.value = parseInt(s.noone_alarm_remaintime) || 30
          if (s.someone_switch !== undefined) someoneSwitch.value = s.someone_switch === '1'
          if (s.someone_high_limit !== undefined) someoneHighLimit.value = parseInt(s.someone_high_limit) || 150
          if (s.device_height_setting !== undefined) deviceHeight.value = parseInt(s.device_height_setting) || 260
          if (s.tumble_alarm_switch !== undefined) tumbleAlarmSwitch.value = s.tumble_alarm_switch === '1'
          if (s.tumble_time_setting !== undefined) tumbleTime.value = parseInt(s.tumble_time_setting) || 30
          if (s.tumble_sensitive_setting !== undefined) tumbleSensitive.value = parseInt(s.tumble_sensitive_setting) || 2
          if (s.paking_switch !== undefined) pakingSwitch.value = s.paking_switch === '1'
          if (s.paking_time_setting !== undefined) pakingTime.value = parseInt(s.paking_time_setting) || 600
        }
      } catch (error) {
        console.error('加载跌倒雷达设置失败:', error)
      }
    }

    // 设备重启
    const rebootDevice = async () => {
      try {
        await ElMessageBox.confirm(
          '确定要重启该跌倒雷达设备吗？重启期间设备将暂时离线。',
          '设备重启',
          { confirmButtonText: '确定重启', cancelButtonText: '取消', type: 'warning' }
        )
        rebootLoading.value = true
        await sendDownstream('reboot')
        ElMessage.success('重启指令已发送')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('重启设备失败:', error)
          ElMessage.error('重启指令发送失败')
        }
      } finally {
        rebootLoading.value = false
      }
    }

    // 无人报警开关
    const toggleNooneAlarm = async (val) => {
      const newVal = val ? 1 : 0
      nooneSwitchLoading.value = true
      try {
        await sendDownstream('noone_switch', newVal)
        nooneSwitch.value = !!newVal
        ElMessage.success(`无人报警已${newVal ? '开启' : '关闭'}`)
        saveDeviceSetting('noone_switch', newVal)
      } catch (error) {
        console.error('切换无人报警失败:', error)
        nooneSwitch.value = !val
        ElMessage.error('操作失败')
      } finally {
        nooneSwitchLoading.value = false
      }
    }

    // 保存无人报警触发时间
    const saveNooneRemaintime = async () => {
      nooneRemaintimeLoading.value = true
      try {
        await sendDownstream('noone_alarm_remaintime', nooneRemaintime.value)
        ElMessage.success(`触发时间已设为${nooneRemaintime.value}分钟`)
        saveDeviceSetting('noone_alarm_remaintime', nooneRemaintime.value)
      } catch (error) {
        console.error('保存触发时间失败:', error)
        ElMessage.error('保存失败')
      } finally {
        nooneRemaintimeLoading.value = false
      }
    }

    // 有人报警开关
    const toggleSomeoneAlarm = async (val) => {
      const newVal = val ? 1 : 0
      someoneSwitchLoading.value = true
      try {
        await sendDownstream('someone_switch', newVal)
        someoneSwitch.value = !!newVal
        ElMessage.success(`有人报警已${newVal ? '开启' : '关闭'}`)
        saveDeviceSetting('someone_switch', newVal)
      } catch (error) {
        console.error('切换有人报警失败:', error)
        someoneSwitch.value = !val
        ElMessage.error('操作失败')
      } finally {
        someoneSwitchLoading.value = false
      }
    }

    // 保存有人探测高限
    const saveSomeoneHighLimit = async () => {
      someoneHighLimitLoading.value = true
      try {
        await sendDownstream('someone_high_limit', someoneHighLimit.value)
        ElMessage.success(`探测高限已设为${someoneHighLimit.value}CM`)
        saveDeviceSetting('someone_high_limit', someoneHighLimit.value)
      } catch (error) {
        console.error('保存探测高限失败:', error)
        ElMessage.error('保存失败')
      } finally {
        someoneHighLimitLoading.value = false
      }
    }

    // 保存吸顶安装高度
    const saveDeviceHeight = async () => {
      deviceHeightLoading.value = true
      try {
        await sendDownstream('device_height_setting', deviceHeight.value)
        ElMessage.success(`安装高度已设为${deviceHeight.value}CM`)
        saveDeviceSetting('device_height_setting', deviceHeight.value)
      } catch (error) {
        console.error('保存安装高度失败:', error)
        ElMessage.error('保存失败')
      } finally {
        deviceHeightLoading.value = false
      }
    }

    // 跌倒报警开关
    const toggleTumbleAlarm = async (val) => {
      const newVal = val ? 1 : 0
      tumbleAlarmSwitchLoading.value = true
      try {
        await sendDownstream('tumble_alarm_switch', newVal)
        tumbleAlarmSwitch.value = !!newVal
        ElMessage.success(`跌倒报警已${newVal ? '开启' : '关闭'}`)
        saveDeviceSetting('tumble_alarm_switch', newVal)
      } catch (error) {
        console.error('切换跌倒报警失败:', error)
        tumbleAlarmSwitch.value = !val
        ElMessage.error('操作失败')
      } finally {
        tumbleAlarmSwitchLoading.value = false
      }
    }

    // 保存跌倒时长
    const saveTumbleTime = async () => {
      tumbleTimeLoading.value = true
      try {
        await sendDownstream('tumble_time_setting', tumbleTime.value)
        ElMessage.success(`跌倒时长已设为${tumbleTime.value}秒`)
        saveDeviceSetting('tumble_time_setting', tumbleTime.value)
      } catch (error) {
        console.error('保存跌倒时长失败:', error)
        ElMessage.error('保存失败')
      } finally {
        tumbleTimeLoading.value = false
      }
    }

    // 保存跌倒灵敏度
    const saveTumbleSensitive = async () => {
      tumbleSensitiveLoading.value = true
      try {
        await sendDownstream('tumble_sensitive_setting', tumbleSensitive.value)
        ElMessage.success(`灵敏度已设为${sensitiveLabels[tumbleSensitive.value]}`)
        saveDeviceSetting('tumble_sensitive_setting', tumbleSensitive.value)
      } catch (error) {
        console.error('保存灵敏度失败:', error)
        ElMessage.error('保存失败')
      } finally {
        tumbleSensitiveLoading.value = false
      }
    }

    // 静止驻留报警开关
    const togglePakingAlarm = async (val) => {
      const newVal = val ? 1 : 0
      pakingSwitchLoading.value = true
      try {
        await sendDownstream('paking_switch', newVal)
        pakingSwitch.value = !!newVal
        ElMessage.success(`静止驻留报警已${newVal ? '开启' : '关闭'}`)
        saveDeviceSetting('paking_switch', newVal)
      } catch (error) {
        console.error('切换静止驻留报警失败:', error)
        pakingSwitch.value = !val
        ElMessage.error('操作失败')
      } finally {
        pakingSwitchLoading.value = false
      }
    }

    // 保存驻留时长
    const savePakingTime = async () => {
      pakingTimeLoading.value = true
      try {
        await sendDownstream('paking_time_setting', pakingTime.value)
        ElMessage.success(`驻留时长已设为${pakingTime.value}秒`)
        saveDeviceSetting('paking_time_setting', pakingTime.value)
      } catch (error) {
        console.error('保存驻留时长失败:', error)
        ElMessage.error('保存失败')
      } finally {
        pakingTimeLoading.value = false
      }
    }

    // ===== 声光报警器方法 =====

    // 加载声光报警器已保存的设置
    const loadAlarmSirenSettings = async () => {
      try {
        const response = await deviceSettingApi.getSettings(device.value.deviceId)
        if (response.success && response.data) {
          const s = response.data
          if (s.alarm_switch !== undefined) alarmSwitch.value = s.alarm_switch === '1'
          if (s.alarm_time_setting !== undefined) alarmTimeSetting.value = parseInt(s.alarm_time_setting) || 0
          if (s.voice_setting !== undefined) voiceSetting.value = parseInt(s.voice_setting) || 3

          // 解析关联设备
          if (s.linked_devices !== undefined && s.linked_devices) {
            try {
              const linkedArr = JSON.parse(s.linked_devices)
              if (Array.isArray(linkedArr)) {
                linkedDevices.value = linkedArr
              }
            } catch (e) {
              console.warn('解析linked_devices失败:', e)
            }
          }
        }

        // 加载家庭设备列表用于关联选择
        await loadFamilyDevicesForLink()
      } catch (error) {
        console.error('加载声光报警器设置失败:', error)
      }
    }

    // 启动/停止报警
    const toggleAlarmSwitch = async () => {
      const newVal = alarmSwitch.value ? 0 : 1
      alarmSwitchLoading.value = true
      try {
        await sendDownstream('alarm_switch', newVal)
        alarmSwitch.value = !!newVal
        ElMessage.success(`报警已${newVal ? '启动' : '停止'}`)
        saveDeviceSetting('alarm_switch', newVal)
      } catch (error) {
        console.error('操作报警开关失败:', error)
        ElMessage.error('操作失败')
      } finally {
        alarmSwitchLoading.value = false
      }
    }

    // 保存报警时长
    const saveAlarmTime = async () => {
      alarmTimeLoading.value = true
      try {
        await sendDownstream('alarm_time_setting', alarmTimeSetting.value)
        ElMessage.success(`报警时长已设为${alarmTimeLabels[alarmTimeSetting.value]}`)
        saveDeviceSetting('alarm_time_setting', alarmTimeSetting.value)
      } catch (error) {
        console.error('保存报警时长失败:', error)
        ElMessage.error('保存失败')
      } finally {
        alarmTimeLoading.value = false
      }
    }

    // 保存音量设置
    const saveVoice = async () => {
      voiceLoading.value = true
      try {
        await sendDownstream('voice_setting', voiceSetting.value)
        ElMessage.success(`音量已设为${voiceLabels[voiceSetting.value]}`)
        saveDeviceSetting('voice_setting', voiceSetting.value)
      } catch (error) {
        console.error('保存音量设置失败:', error)
        ElMessage.error('保存失败')
      } finally {
        voiceLoading.value = false
      }
    }

    // 加载家庭设备列表（用于关联设备选择）
    const loadFamilyDevicesForLink = async () => {
      if (!device.value.familyId) return
      try {
        const response = await api.get(`/devices/family/${device.value.familyId}`)
        if (response.success && response.data) {
          const currentDeviceId = device.value.deviceId
          familyDeviceList.value = (response.data || [])
            .filter(d => d.deviceId !== currentDeviceId)
            .map(d => ({
              deviceId: d.deviceId,
              deviceName: d.deviceName || d.deviceId,
              deviceTypeName: d.deviceTypeName || '未知类型',
              status: d.status
            }))
        }
      } catch (error) {
        console.error('加载家庭设备列表失败:', error)
      }
    }

    // 保存关联设备
    const saveLinkedDevices = async () => {
      linkedDeviceSaving.value = true
      try {
        const jsonStr = JSON.stringify(linkedDevices.value)
        await deviceSettingApi.saveSetting(device.value.deviceId, 'linked_devices', jsonStr)
        ElMessage.success(`已关联${linkedDevices.value.length}台设备`)
      } catch (error) {
        console.error('保存关联设备失败:', error)
        ElMessage.error('保存失败')
      } finally {
        linkedDeviceSaving.value = false
      }
    }

    // ===== 通用方法 =====

    const muteDevice = async () => {
      try {
        await ElMessageBox.confirm(
          `确定要对设备 "${device.value.deviceName}" 发送消音指令吗？`,
          '设备消音',
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        )

        muteLoading.value = true
        commandResult.value = null
        const response = await api.post(`/devices/${device.value.deviceId}/mute`, {})
        commandResult.value = response
        if (response.success) {
          ElMessage.success('消音指令发送成功')
        } else {
          ElMessage.error(response.message || '消音指令发送失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('消音操作失败:', error)
          ElMessage.error('消音操作失败')
        }
      } finally {
        muteLoading.value = false
      }
    }

    const resetDevice = async () => {
      try {
        await ElMessageBox.confirm(
          `确定要对设备 "${device.value.deviceName}" 发送复位指令吗？复位后设备将重启。`,
          '设备复位',
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        )

        resetLoading.value = true
        commandResult.value = null
        const response = await api.post(`/devices/${device.value.deviceId}/reset`, {})
        commandResult.value = response
        if (response.success) {
          ElMessage.success('复位指令发送成功')
        } else {
          ElMessage.error(response.message || '复位指令发送失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('复位操作失败:', error)
          ElMessage.error('复位操作失败')
        }
      } finally {
        resetLoading.value = false
      }
    }

    const queryDevice = async () => {
      queryLoading.value = true
      commandResult.value = null
      try {
        const response = await api.post(`/devices/${device.value.deviceId}/query`, {})
        commandResult.value = response
        if (response.success) {
          ElMessage.success('查询指令发送成功')
        } else {
          ElMessage.error(response.message || '查询指令发送失败')
        }
      } catch (error) {
        console.error('查询操作失败:', error)
        ElMessage.error('查询操作失败')
      } finally {
        queryLoading.value = false
      }
    }

    const refreshDevice = () => {
      commandResult.value = null
      loadDevice()
    }

    const goBack = () => {
      router.back()
    }

    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-'
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      })
    }

    const getBatteryColor = (level) => {
      if (level > 60) return '#67c23a'
      if (level > 30) return '#e6a23c'
      return '#f56c6c'
    }

    const formatCommand = (command) => {
      if (!command) return '未配置'
      return JSON.stringify(command, null, 2)
    }

    const getStatusType = (status) => {
      const deviceStatus = Number(status)
      if (deviceStatus === 1) return 'success'
      if (deviceStatus === 0) return 'danger'
      return 'info'
    }

    const getStatusText = (status) => {
      const deviceStatus = Number(status)
      if (deviceStatus === 1) return '在线'
      if (deviceStatus === 0) return '离线'
      return '未知'
    }

    const formatSignalStrength = (rsrp) => {
      if (!rsrp) return '无效值'
      return `${rsrp} dBm`
    }

    const getSignalQualityType = (rsrp) => {
      if (!rsrp) return 'info'
      const dbValue = Number(rsrp)
      if (dbValue > -70) return 'success'
      if (dbValue >= -90) return 'success'
      if (dbValue >= -110) return 'warning'
      return 'danger'
    }

    const getSignalQualityText = (rsrp) => {
      if (!rsrp) return '无效值'
      const dbValue = Number(rsrp)
      if (dbValue > -70) return '优秀'
      if (dbValue >= -90) return '良好'
      if (dbValue >= -110) return '一般'
      return '较差'
    }

    // ===== 设备定位方法 =====
    const loadDevicePosition = async () => {
      try {
        const response = await api.get(`/devices/${deviceId}/position/latest`)
        if (response.success && response.data) {
          devicePosition.value = response.data
          // 如果有坐标，延迟初始化地图（等待DOM渲染）
          if (response.data.latitude && response.data.longitude) {
            setTimeout(() => {
              initPositionMap(response.data.latitude, response.data.longitude)
              // 初始化围栏管理地图（DOM已渲染）
              initGeofenceMap()
            }, 500)
          } else {
            // 无坐标时也初始化围栏管理地图
            setTimeout(() => initGeofenceMap(), 500)
          }
        }
      } catch (error) {
        console.log('该设备暂无定位数据')
      }
    }

    const loadPositionHistory = async () => {
      try {
        positionLoading.value = true
        const page = positionHistoryPage.value
        const response = await api.get(`/devices/${deviceId}/position/history?page=${page}&size=${positionHistorySize}`)
        if (response.success && response.data) {
          positionHistory.value = response.data
          positionHistoryTotal.value = response.total || response.data.length
          // 如果有历史轨迹，绘制到地图上
          if (positionMapInstance.value && response.data.length > 1) {
            drawPositionTrack(response.data)
          }
        }
      } catch (error) {
        console.error('加载定位历史失败:', error)
      } finally {
        positionLoading.value = false
      }
    }

    const onPositionPageChange = (page) => {
      positionHistoryPage.value = page
      loadPositionHistory()
    }

    const onHistoryRowClick = (row) => {
      if (!row.latitude || !row.longitude || !positionMapInstance.value) return
      const map = positionMapInstance.value
      const pos = new window.TMap.LatLng(row.latitude, row.longitude)

      // 移动地图中心到选中位置
      map.setCenter(pos)

      // 清除之前的选中标记
      if (positionInfoWindow.value) {
        positionInfoWindow.value.close()
      }

      // 显示 InfoWindow 标注选中的历史位置
      const time = formatPositionTime(row.createdAt)
      positionInfoWindow.value = new window.TMap.InfoWindow({
        map: map,
        position: pos,
        content: `<div style="padding:4px 8px;font-size:12px;"><div style="font-weight:bold;color:#409eff;">${row.accessTypeName || ''}定位</div><div>${time}</div>${row.powerPercent != null ? '<div>电量 ' + row.powerPercent + '%</div>' : ''}</div>`,
        offset: { x: 0, y: -10 }
      })
    }

    const resolvePosition = async () => {
      try {
        positionResolving.value = true
        positionResolveMsg.value = ''
        const response = await api.post(`/devices/${deviceId}/position/resolve`)
        if (response.success && response.data && response.data.latitude && response.data.longitude) {
          devicePosition.value = response.data
          positionResolveMsg.value = ''
          setTimeout(() => {
            initPositionMap(response.data.latitude, response.data.longitude)
          }, 500)
        } else {
          positionResolveMsg.value = response.message || '解析失败，请查看后端日志'
        }
      } catch (error) {
        positionResolveMsg.value = '请求失败: ' + (error.message || error)
      } finally {
        positionResolving.value = false
      }
    }

    const initPositionMap = (lat, lng) => {
      const container = document.getElementById('position-map-container')
      if (!container || !window.TMap) {
        console.warn('地图容器或TMap SDK未就绪')
        return
      }

      // 销毁已有地图实例
      if (positionMapInstance.value) {
        positionMapInstance.value.destroy()
      }

      const center = new window.TMap.LatLng(lat, lng)
      const map = new window.TMap.Map('position-map-container', {
        center: center,
        zoom: 15
      })

      const deviceName = device.value.deviceName || '设备位置'

      // 使用 MultiMarker 添加标记
      const marker = new window.TMap.MultiMarker({
        map: map,
        styles: {
          default: new window.TMap.MarkerStyle({
            width: 25,
            height: 35,
            anchor: { x: 12, y: 35 }
          })
        },
        geometries: [{
          id: 'device',
          styleId: 'default',
          position: center
        }]
      })

      // 使用 InfoWindow 显示设备名称
      const infoWindow = new window.TMap.InfoWindow({
        map: map,
        position: center,
        content: `<div style="padding:2px 6px;background:#409eff;color:#fff;border-radius:4px;font-size:12px;white-space:nowrap;">${deviceName}</div>`,
        offset: { x: 0, y: -38 }
      })

      positionMapInstance.value = map
      positionMarker.value = marker
    }

    const drawPositionTrack = (positions) => {
      if (!positionMapInstance.value) return
      const path = positions
        .filter(p => p.latitude && p.longitude)
        .map(p => new window.TMap.LatLng(p.latitude, p.longitude))

      if (path.length < 2) return

      new window.TMap.MultiPolyline({
        map: positionMapInstance.value,
        styles: {
          default: new window.TMap.PolylineStyle({
            color: '#409eff',
            width: 4,
            lineCap: 'round'
          })
        },
        geometries: [{
          styleId: 'default',
          paths: path
        }]
      })

      // 自适应视野
      const bounds = new window.TMap.LatLngBounds()
      path.forEach(p => bounds.extend(p))
      positionMapInstance.value.fitBounds(bounds, { padding: 50 })
    }

    // ===== 智能胸卡定时语音方法 =====

    const loadVoiceSchedules = async () => {
      try {
        const response = await api.get(`/devices/${deviceId}/voice-schedules`)
        if (response.success && response.data) {
          voiceSchedules.value = response.data.map(s => {
            // timerWeek 掩码转 weekDays 数组
            const weekDays = []
            weekDayOptions.forEach(opt => {
              if (s.timerWeek & opt.value) weekDays.push(opt.value)
            })
            // timerHour + timerMinute → timeValue (Date)
            const d = new Date()
            d.setHours(s.timerHour, s.timerMinute, 0, 0)
            return {
              ...s,
              weekDays,
              timeValue: d,
              saving: false,
              deleting: false
            }
          })
        }
      } catch (error) {
        console.error('加载定时语音配置失败:', error)
      }
    }

    const saveVoiceSchedule = async (item) => {
      if (!item.timeValue) {
        ElMessage.warning('请选择时间')
        return
      }
      if (!item.weekDays || item.weekDays.length === 0) {
        ElMessage.warning('请选择星期')
        return
      }
      item.saving = true
      try {
        const time = new Date(item.timeValue)
        // weekDays 数组合并为 timerWeek 掩码
        const timerWeek = item.weekDays.reduce((mask, v) => mask | v, 0)
        const response = await api.post(`/devices/${deviceId}/voice-schedules`, {
          timerId: item.timerId,
          timerHour: time.getHours(),
          timerMinute: time.getMinutes(),
          timerWeek,
          timerContent: item.timerContent || '',
          timerSwitch: item.timerSwitch
        })
        if (response.success) {
          ElMessage.success('定时语音保存成功')
          await loadVoiceSchedules()
        } else {
          ElMessage.error(response.message || '保存失败')
        }
      } catch (error) {
        console.error('保存定时语音失败:', error)
        ElMessage.error('保存定时语音失败')
      } finally {
        item.saving = false
      }
    }

    const deleteVoiceSchedule = async (item, index) => {
      if (!item.id) {
        // 尚未保存到数据库的本地记录，直接移除
        voiceSchedules.value.splice(index, 1)
        return
      }
      try {
        await ElMessageBox.confirm(`确定要删除定时${item.timerId}吗？`, '删除定时语音', {
          type: 'warning'
        })
        item.deleting = true
        const response = await api.delete(`/devices/${deviceId}/voice-schedules/${item.id}`)
        if (response.success) {
          ElMessage.success('定时语音删除成功')
          await loadVoiceSchedules()
        } else {
          ElMessage.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除定时语音失败:', error)
          ElMessage.error('删除定时语音失败')
        }
      } finally {
        item.deleting = false
      }
    }

    const addVoiceSchedule = () => {
      // 找到下一个可用的 timerId (1-5)
      const usedIds = voiceSchedules.value.map(s => s.timerId)
      let nextId = 1
      for (let i = 1; i <= 5; i++) {
        if (!usedIds.includes(i)) {
          nextId = i
          break
        }
      }
      const d = new Date()
      d.setHours(8, 0, 0, 0)
      voiceSchedules.value.push({
        id: null,
        timerId: nextId,
        timerHour: 8,
        timerMinute: 0,
        timerWeek: 0x7F,
        timerContent: '',
        timerSwitch: 1,
        weekDays: [0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40],
        timeValue: d,
        saving: false,
        deleting: false
      })
    }

    // ===== 围栏管理方法 =====
    const loadGeofences = async () => {
      try {
        const response = await api.get(`/devices/${deviceId}/geofences`)
        if (response.success && response.data) {
          geofenceList.value = response.data
          highlightedGeofenceId.value = null
          // 在定位地图上渲染已有围栏
          renderGeofencesOnPositionMap()
          // 在围栏绘制地图上渲染已有围栏（如果已初始化）
          if (geofenceMapInstance.value) {
            renderGeofencesOnGeofenceMap()
          }
        }
      } catch (error) {
        console.error('加载围栏列表失败:', error)
      }
    }

    const addCircleGeofence = async () => {
      if (!devicePosition.value || !devicePosition.value.latitude) {
        ElMessage.warning('设备无定位数据，无法创建围栏')
        return
      }
      geofenceSaving.value = true
      try {
        const response = await api.post(`/devices/${deviceId}/geofences`, {
          name: geofenceName.value || '圆形围栏',
          fenceType: 'circle',
          centerLat: devicePosition.value.latitude,
          centerLng: devicePosition.value.longitude,
          radius: geofenceRadius.value
        })
        if (response.success) {
          ElMessage.success('圆形围栏创建成功')
          geofenceName.value = ''
          geofenceRadius.value = 500
          await loadGeofences()
        } else {
          ElMessage.error(response.message || '创建失败')
        }
      } catch (error) {
        ElMessage.error('创建围栏失败')
      } finally {
        geofenceSaving.value = false
      }
    }

    const savePolygonGeofence = async () => {
      if (polygonPoints.value.length < 3) {
        ElMessage.warning('至少需要3个顶点')
        return
      }
      // 检查设备位置是否在多边形内
      if (!polygonContainsDevice.value) {
        try {
          await ElMessageBox.confirm(
            '设备当前位置不在绘制的围栏范围内，保存后设备将立即触发越界报警。确定要保存吗？',
            '围栏未包含设备位置',
            { type: 'warning', confirmButtonText: '仍然保存', cancelButtonText: '返回修改' }
          )
        } catch {
          return
        }
      }
      geofenceSaving.value = true
      try {
        const polygonPath = JSON.stringify(polygonPoints.value.map(p => [p.lat, p.lng]))
        const response = await api.post(`/devices/${deviceId}/geofences`, {
          name: geofenceName.value || '多边形围栏',
          fenceType: 'polygon',
          polygonPath: polygonPath
        })
        if (response.success) {
          ElMessage.success('多边形围栏创建成功')
          geofenceName.value = ''
          clearPolygonPoints()
          await loadGeofences()
        } else {
          ElMessage.error(response.message || '创建失败')
        }
      } catch (error) {
        ElMessage.error('创建围栏失败')
      } finally {
        geofenceSaving.value = false
      }
    }

    const deleteGeofence = async (fence) => {
      try {
        await ElMessageBox.confirm(`确定要删除围栏「${fence.name}」吗？`, '删除围栏', {
          type: 'warning'
        })
        const response = await api.delete(`/devices/${deviceId}/geofences/${fence.id}`)
        if (response.success) {
          ElMessage.success('围栏已删除')
          await loadGeofences()
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    }

    const getPolygonPointCount = (polygonPath) => {
      try {
        return JSON.parse(polygonPath).length
      } catch {
        return 0
      }
    }

    // 初始化多边形绘制地图
    const initGeofenceMap = () => {
      const container = document.getElementById('geofence-map-container')
      if (!container || !window.TMap) return

      if (geofenceMapInstance.value) {
        geofenceMapInstance.value.destroy()
      }

      const lat = devicePosition.value ? devicePosition.value.latitude : 39.9
      const lng = devicePosition.value ? devicePosition.value.longitude : 116.4
      const center = new window.TMap.LatLng(lat, lng)

      const map = new window.TMap.Map('geofence-map-container', {
        center: center,
        zoom: 15
      })

      // 地图点击事件 - 仅在多边形模式下添加顶点
      map.on('click', (evt) => {
        if (geofenceFenceType.value !== 'polygon') return
        const point = { lat: evt.latLng.getLat(), lng: evt.latLng.getLng() }
        polygonPoints.value.push(point)
        checkPolygonContainsDevice()
        updatePolygonPreview()
      })

      // 双击结束绘制（阻止默认缩放）
      map.on('dblclick', (evt) => {
        evt.originalEvent && evt.originalEvent.preventDefault && evt.originalEvent.preventDefault()
      })

      geofenceMapInstance.value = map

      // 标记设备当前位置
      if (lat && lng) {
        new window.TMap.MultiMarker({
          map: map,
          styles: {
            device: new window.TMap.MarkerStyle({
              width: 20,
              height: 28,
              anchor: { x: 10, y: 28 }
            })
          },
          geometries: [{
            id: 'device-pos',
            styleId: 'device',
            position: center
          }]
        })
      }

      // 在围栏绘制地图上渲染已有围栏
      renderGeofencesOnGeofenceMap()
    }

    const updatePolygonPreview = () => {
      const map = geofenceMapInstance.value
      if (!map || !window.TMap) return

      // 清除旧图层
      if (geofencePolygonLayer.value) {
        geofencePolygonLayer.value.setMap(null)
        geofencePolygonLayer.value = null
      }
      if (geofenceMarkerLayer.value) {
        geofenceMarkerLayer.value.setMap(null)
        geofenceMarkerLayer.value = null
      }

      const points = polygonPoints.value
      if (points.length === 0) return

      // 绘制顶点标记
      geofenceMarkerLayer.value = new window.TMap.MultiMarker({
        map: map,
        styles: {
          vertex: new window.TMap.MarkerStyle({
            width: 12,
            height: 12,
            anchor: { x: 6, y: 6 },
            src: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"><circle cx="6" cy="6" r="5" fill="#409eff" stroke="#fff" stroke-width="2"/></svg>')
          })
        },
        geometries: points.map((p, i) => ({
          id: 'v' + i,
          styleId: 'vertex',
          position: new window.TMap.LatLng(p.lat, p.lng)
        }))
      })

      // 绘制多边形预览（至少3个点）
      if (points.length >= 3) {
        geofencePolygonLayer.value = new window.TMap.MultiPolygon({
          map: map,
          styles: {
            default: new window.TMap.PolygonStyle({
              color: 'rgba(64,158,255,0.15)',
              showBorder: true,
              borderColor: '#409eff',
              borderWidth: 2
            })
          },
          geometries: [{
            id: 'preview',
            styleId: 'default',
            paths: [points.map(p => new window.TMap.LatLng(p.lat, p.lng))]
          }]
        })
      }
    }

    const undoPolygonPoint = () => {
      polygonPoints.value.pop()
      checkPolygonContainsDevice()
      updatePolygonPreview()
    }

    const clearPolygonPoints = () => {
      polygonPoints.value = []
      polygonContainsDevice.value = false
      updatePolygonPreview()
    }

    // 检查设备当前位置是否在绘制的多边形内
    const checkPolygonContainsDevice = () => {
      const pos = devicePosition.value
      const points = polygonPoints.value
      if (!pos || !pos.latitude || !pos.longitude || points.length < 3) {
        polygonContainsDevice.value = false
        return
      }
      polygonContainsDevice.value = isPointInPolygon(pos.latitude, pos.longitude, points)
    }

    // 射线法判断点是否在多边形内
    const isPointInPolygon = (lat, lng, points) => {
      let inside = false
      for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const yi = points[i].lat, xi = points[i].lng
        const yj = points[j].lat, xj = points[j].lng
        if ((yi > lat) !== (yj > lat) &&
            lng < (xj - xi) * (lat - yi) / (yj - yi) + xi) {
          inside = !inside
        }
      }
      return inside
    }

    // 在定位地图上渲染已有围栏
    const renderGeofencesOnPositionMap = () => {
      const map = positionMapInstance.value
      if (!map || !window.TMap) return

      geofenceList.value.forEach((fence, idx) => {
        if (fence.fenceType === 'polygon' && fence.polygonPath) {
          try {
            const points = JSON.parse(fence.polygonPath)
            new window.TMap.MultiPolygon({
              map: map,
              styles: {
                default: new window.TMap.PolygonStyle({
                  color: 'rgba(230,162,60,0.12)',
                  showBorder: true,
                  borderColor: '#e6a23c',
                  borderWidth: 2
                })
              },
              geometries: [{
                id: 'fence-' + idx,
                styleId: 'default',
                paths: [points.map(p => new window.TMap.LatLng(p[0], p[1]))]
              }]
            })
          } catch (e) { /* ignore parse error */ }
        }
      })
    }

    // 在围栏绘制地图上渲染已有围栏（支持高亮）
    const renderGeofencesOnGeofenceMap = () => {
      const map = geofenceMapInstance.value
      if (!map || !window.TMap) return

      // 清除旧的已有围栏图层
      geofenceExistingLayers.value.forEach(layer => {
        try { layer.setMap(null) } catch (e) { /* ignore */ }
      })
      geofenceExistingLayers.value = []

      const highlightId = highlightedGeofenceId.value

      geofenceList.value.forEach((fence, idx) => {
        const isHighlight = fence.id === highlightId

        if (fence.fenceType === 'polygon' && fence.polygonPath) {
          try {
            const points = JSON.parse(fence.polygonPath)
            if (!points || points.length < 3) return

            const layer = new window.TMap.MultiPolygon({
              map: map,
              styles: {
                default: new window.TMap.PolygonStyle({
                  color: isHighlight ? 'rgba(245,108,108,0.25)' : 'rgba(230,162,60,0.12)',
                  showBorder: true,
                  borderColor: isHighlight ? '#f56c6c' : '#e6a23c',
                  borderWidth: isHighlight ? 3 : 2
                })
              },
              geometries: [{
                id: 'existing-fence-' + idx,
                styleId: 'default',
                paths: [points.map(p => new window.TMap.LatLng(p[0], p[1]))]
              }]
            })
            geofenceExistingLayers.value.push(layer)

            // 高亮围栏添加名称标注
            if (isHighlight) {
              const centerLat = points.reduce((s, p) => s + p[0], 0) / points.length
              const centerLng = points.reduce((s, p) => s + p[1], 0) / points.length
              const infoWindow = new window.TMap.InfoWindow({
                map: map,
                position: new window.TMap.LatLng(centerLat, centerLng),
                content: `<div style="padding:2px 8px;background:#f56c6c;color:#fff;border-radius:4px;font-size:12px;white-space:nowrap;">${fence.name}</div>`,
                offset: { x: 0, y: 0 }
              })
              geofenceExistingLayers.value.push(infoWindow)
            }
          } catch (e) { /* ignore */ }
        } else if (fence.fenceType !== 'polygon' && fence.centerLat && fence.centerLng) {
          // 圆形围栏：用 InfoWindow 标注中心点和半径
          const center = new window.TMap.LatLng(fence.centerLat, fence.centerLng)
          const label = isHighlight
            ? `<div style="padding:2px 8px;background:#f56c6c;color:#fff;border-radius:4px;font-size:12px;white-space:nowrap;">${fence.name}（半径${fence.radius}米）</div>`
            : `<div style="padding:2px 8px;background:#e6a23c;color:#fff;border-radius:4px;font-size:12px;white-space:nowrap;">${fence.name}（${fence.radius}m）</div>`
          const infoWindow = new window.TMap.InfoWindow({
            map: map,
            position: center,
            content: label,
            offset: { x: 0, y: 0 }
          })
          geofenceExistingLayers.value.push(infoWindow)
        }
      })
    }

    // 围栏列表行点击 —— 高亮并定位
    const onGeofenceRowClick = (row) => {
      // 切换高亮
      highlightedGeofenceId.value = highlightedGeofenceId.value === row.id ? null : row.id

      const map = geofenceMapInstance.value

      // 如果围栏绘制地图存在，重新渲染高亮
      if (map) {
        renderGeofencesOnGeofenceMap()
        // 移动地图到选中围栏位置
        if (highlightedGeofenceId.value) {
          if (row.fenceType === 'polygon' && row.polygonPath) {
            try {
              const points = JSON.parse(row.polygonPath)
              const centerLat = points.reduce((s, p) => s + p[0], 0) / points.length
              const centerLng = points.reduce((s, p) => s + p[1], 0) / points.length
              map.setCenter(new window.TMap.LatLng(centerLat, centerLng))
            } catch (e) { /* ignore */ }
          } else if (row.centerLat && row.centerLng) {
            map.setCenter(new window.TMap.LatLng(row.centerLat, row.centerLng))
          }
        }
      }

      // 同时在定位地图上也做定位（如果存在）
      const posMap = positionMapInstance.value
      if (posMap && highlightedGeofenceId.value) {
        if (row.fenceType === 'polygon' && row.polygonPath) {
          try {
            const points = JSON.parse(row.polygonPath)
            const centerLat = points.reduce((s, p) => s + p[0], 0) / points.length
            const centerLng = points.reduce((s, p) => s + p[1], 0) / points.length
            posMap.setCenter(new window.TMap.LatLng(centerLat, centerLng))
          } catch (e) { /* ignore */ }
        } else if (row.centerLat && row.centerLng) {
          posMap.setCenter(new window.TMap.LatLng(row.centerLat, row.centerLng))
        }
      }
    }

    // watch 围栏类型切换时，地图点击事件只在polygon模式下生效（通过 onGeofenceMapClick 判断）
    // 地图始终显示，切换类型时无需重新初始化
    watch(geofenceFenceType, () => {
      // 切换到非polygon时清除绘制中的点
      if (geofenceFenceType.value !== 'polygon') {
        polygonPoints.value = []
        polygonContainsDevice.value = false
        updatePolygonPreview()
      }
    })

    const formatPositionTime = (dateStr) => {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }

    onMounted(() => {
      loadDevice()
    })

    return {
      loading, muteLoading, resetLoading, queryLoading,
      device, productConfig, commandResult, isGateway,
      // 事件通知策略
      eventPolicies, eventPolicyLoading, availablePolicies,
      loadEventPolicies, saveEventPolicyInline, getEventTypeName,
      // 跌倒雷达控制
      isTumbleRadar,
      nooneSwitch, nooneSwitchLoading, nooneRemaintime, nooneRemaintimeLoading,
      someoneSwitch, someoneSwitchLoading, someoneHighLimit, someoneHighLimitLoading,
      deviceHeight, deviceHeightLoading,
      tumbleAlarmSwitch, tumbleAlarmSwitchLoading, tumbleTime, tumbleTimeLoading,
      tumbleSensitive, tumbleSensitiveLoading, sensitiveLabels,
      pakingSwitch, pakingSwitchLoading, pakingTime, pakingTimeLoading,
      rebootLoading,
      rebootDevice, toggleNooneAlarm, saveNooneRemaintime,
      toggleSomeoneAlarm, saveSomeoneHighLimit, saveDeviceHeight,
      toggleTumbleAlarm, saveTumbleTime, saveTumbleSensitive,
      togglePakingAlarm, savePakingTime,
      // 声光报警器控制
      isAlarmSiren,
      alarmSwitch, alarmSwitchLoading,
      alarmTimeSetting, alarmTimeLoading, alarmTimeLabels,
      voiceSetting, voiceLoading, voiceLabels,
      toggleAlarmSwitch, saveAlarmTime, saveVoice,
      linkedDevices, familyDeviceList, linkedDeviceSaving, saveLinkedDevices,
      // 智能胸卡定时语音
      isSmartCard, voiceSchedules, weekDayOptions,
      loadVoiceSchedules, saveVoiceSchedule, deleteVoiceSchedule, addVoiceSchedule,
      // 设备定位
      devicePosition, positionHistory, positionLoading,
      positionResolving, positionResolveMsg, resolvePosition,
      positionHistoryPage, positionHistorySize, positionHistoryTotal,
      loadPositionHistory, formatPositionTime,
      onPositionPageChange, onHistoryRowClick,
      // 围栏管理
      geofenceList, geofenceFenceType, geofenceName, geofenceRadius,
      geofenceSaving, polygonPoints, polygonContainsDevice,
      addCircleGeofence, savePolygonGeofence, deleteGeofence,
      undoPolygonPoint, clearPolygonPoints, getPolygonPointCount,
      onGeofenceRowClick,
      // 通用
      loadDevice, muteDevice, resetDevice, queryDevice,
      refreshDevice, goBack,
      formatDateTime, getBatteryColor, formatCommand,
      getStatusType, getStatusText,
      formatSignalStrength, getSignalQualityType, getSignalQualityText
    }
  }
}
</script>

<style scoped>
.device-detail {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

pre {
  background-color: #f5f7fa;
  padding: 10px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  overflow-x: auto;
}

.tumble-control-group {
  padding: 4px 0;
}

.tumble-control-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.tumble-label {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.tumble-sub-label {
  font-size: 13px;
  color: #606266;
}

.tumble-value {
  font-size: 13px;
  color: #909399;
}

.tumble-sub-control {
  padding: 8px 0 0 0;
}

.tumble-slider-row {
  display: flex;
  align-items: center;
  padding: 0 4px;
}

.position-map-container {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
}

.position-no-coords {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
  background: #f5f7fa;
  border-radius: 8px;
}

.position-no-coords p {
  margin: 8px 0 0;
  color: #909399;
}

.geofence-map-container {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
}

.geofence-draw-tip {
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #ecf5ff;
  color: #409eff;
  font-size: 13px;
  border-radius: 4px;
  border-left: 3px solid #409eff;
}

.geofence-form {
  margin-bottom: 12px;
}
</style>
